import type { AgentCard } from '@ralph/contracts'
import clsx from 'clsx'
import { BookOpen, Brain, ShieldCheck, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/primitives.tsx'
import { useI18n, useT } from '@/i18n/index.ts'
import { cardBadges } from './agent-identity.ts'

/**
 * Capability badges, derived from the resolved configuration (AGM-001).
 *
 * Nothing here is typed by a maker: the server computes the three booleans from what the
 * agent actually resolves to, so a listing cannot advertise knowledge it does not have. The
 * badges are also deliberately not colour-coded by kind — colour in this design system means
 * live/grounded/failure, and a decorative palette would dilute all three.
 *
 * Their glyphs are drawn a full step heavier than the icons in running text. At 10px a 2px
 * stroke thins to something like a hairline once it is antialiased, and the badge then reads
 * as a smudge with a word next to it rather than as a labelled mark.
 */
export const AgentBadges = ({ badges, className }: { badges: AgentCard['badges']; className?: string }) => {
  // Bound here rather than captured at module scope: the base count is a plural, and a
  // frozen locale would keep choosing English forms after a switch (PRD-27 F-3).
  const i18n = useI18n()
  const t = i18n.t.marketplace.gallery.card
  const items = cardBadges(badges)
  if (items.length === 0) return null

  return (
    <span className={clsx('flex flex-wrap items-center gap-1.5', className)}>
      {items.map((badge) =>
        badge.kind === 'knowledge' ? (
          <Badge key="knowledge">
            <BookOpen size={10} strokeWidth={2.4} aria-hidden />
            {t.knowledgeBadge(i18n, badge.count)}
          </Badge>
        ) : badge.kind === 'actions' ? (
          <Badge key="actions">
            <Zap size={10} strokeWidth={2.4} aria-hidden />
            {t.actionsBadge}
          </Badge>
        ) : (
          <Badge key="memory">
            <Brain size={10} strokeWidth={2.4} aria-hidden />
            {t.memoryBadge}
          </Badge>
        )
      )}
    </span>
  )
}

/**
 * The trust mark. Grantable only by an administrator (AGM-005), which is why it is a
 * component of its own rather than one more entry in the badge list — a maker-reachable
 * badge and an administrator-granted one must not look interchangeable in the code either.
 *
 * It wears the grounded tone rather than the primary: the mark is a claim about provenance,
 * which is the same channel a resolved citation speaks in, and the primary belongs to the
 * model and to actions.
 */
export const OfficialMark = () => {
  // Read in the body like every other catalogue access: a module-scope capture would freeze
  // the title in whichever language imported this file first (PRD-27 F-3, LOC-012).
  const t = useT().marketplace.gallery.card

  return (
    // The title says what the mark MEANS. "Official" on its own invites everyone to read their
    // own guarantee into it, which is exactly the ambiguity a badge-mimicking name would exploit.
    <span title={t.officialTitle}>
      <Badge tone="grounded" className="shrink-0">
        <ShieldCheck size={10} strokeWidth={2.4} aria-hidden />
        {t.officialLabel}
      </Badge>
    </span>
  )
}
